import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import Can from 'components/Can';
import {
  Box,
  List,
  Paper,
  Divider,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemButton,
} from '@mui/material';

/**
 * Selected item on noc calendar
 */
const SelectedItemPopup = ({
  item,
  position,
  requestType,
  handleClose,
  goToReservation,
  handleChangeReservationTimes,
  handleChangeVehicleOnReservation,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    /**
     * Alert if clicked on outside of element and close the popup
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        handleClose();
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handleClose]);

  return (
    <Box ref={ref} zIndex={1000} position="fixed" top={position.y + 20} left={position.x - 20}>
      <Paper elevation={3} position="relative">
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          subheader={
            <ListSubheader
              component="div"
              id="nested-list-subheader"
              sx={{ textTransform: 'capitalize' }}
            >
              {`${item.id} - ${item?.user?.name || item?.user?.first_name || ''}`}
            </ListSubheader>
          }
          sx={{ margin: 0 }}
        >
          {requestType !== 'on_demand' && !['COMPLETED'].includes(item.status) && (
            <Can I="update_vehicle" a="reservation">
              <ListItem component="div" sx={{ padding: 0 }} disableGutters disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleChangeVehicleOnReservation(item);
                    handleClose();
                  }}
                >
                  <ListItemText
                    disableTypography
                    style={{ fontSize: 14 }}
                    primary={i18n.t('dashboard.noc_change_vehicle')}
                  />
                </ListItemButton>
              </ListItem>
            </Can>
          )}
          {requestType !== 'on_demand' && !['COMPLETED'].includes(item.status) && (
            <Can I="update_times" a="reservation">
              <ListItem component="div" sx={{ padding: 0 }} disableGutters disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleChangeReservationTimes(item);
                    handleClose();
                  }}
                >
                  <ListItemText
                    disableTypography
                    style={{ fontSize: 14 }}
                    primary={i18n.t('dashboard.noc_change_times')}
                  />
                </ListItemButton>
              </ListItem>
            </Can>
          )}
          {Object.prototype.hasOwnProperty.call(item, 'status') &&
            !['OFFLINE'].includes(item.status) && (
              <>
                <Divider />
                <Can I="read" a="reservation">
                  <ListItem component="div" sx={{ padding: 0 }} disableGutters disablePadding>
                    <ListItemButton
                      onClick={() => {
                        goToReservation(item);
                      }}
                    >
                      <ListItemText
                        disableTypography
                        style={{ fontSize: 14 }}
                        primary={i18n.t('dashboard.noc_go_to_reservation')}
                      />
                    </ListItemButton>
                  </ListItem>
                </Can>
              </>
            )}
        </List>
      </Paper>
      <Box sx={{ left: 10, position: 'absolute', top: -24 }}>
        <Box
          sx={{
            boxShadow: '0 16px 10px -17px rgba(0, 0, 0, 0.5)',
            height: 25,
            overflow: 'hidden',
            position: 'relative',
            width: 25,
            zIndex: 1000,
            '&::after': {
              background: '#FFF',
              boxShadow: '-1px -1px 10px -2px rgba(0, 0, 0, 0.5)',
              content: "''",
              height: 12.5,
              left: 6.25,
              position: 'absolute',
              top: 18.75,
              transform: 'rotate(45deg)',
              width: 12.5,
            },
          }}
        />
      </Box>
    </Box>
  );
};

SelectedItemPopup.propTypes = {
  item: PropTypes.object.isRequired,
  position: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  requestType: PropTypes.string.isRequired,
  goToReservation: PropTypes.func.isRequired,
  handleChangeReservationTimes: PropTypes.func.isRequired,
  handleChangeVehicleOnReservation: PropTypes.func.isRequired,
};

export default SelectedItemPopup;
